import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Emergent-managed Google Auth callback.
 * Reads ?session_id= from URL fragment, exchanges it via backend bridge,
 * then redirects to the Supabase magic-link to finalise login.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || '';
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;
        if (!sessionId) {
            setError("Lien d'authentification invalide ou expiré.");
            return;
        }

        // Redirect target after Supabase magic-link consumption
        const redirectTo = window.location.origin + '/app/dashboard';

        axios.post(`${API}/auth/emergent/bridge`, {
            session_id: sessionId,
            redirect_to: redirectTo,
        })
            .then((r) => {
                const action_link = r.data?.action_link;
                if (!action_link) throw new Error('Lien d\'activation absent');
                // Clean URL hash before leaving (security hygiene)
                window.history.replaceState({}, '', window.location.pathname);
                // Hard redirect to Supabase magic-link — Supabase will then redirect to /app/dashboard
                window.location.href = action_link;
            })
            .catch((err) => {
                setError(err?.response?.data?.detail || err?.message || 'Connexion Google échouée');
            });
    }, []);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="max-w-md w-full card-soft p-10 text-center" data-testid="auth-callback">
                {!error ? (
                    <>
                        <Loader2 size={40} className="mx-auto animate-spin text-primary mb-5" />
                        <h1 className="font-heading text-2xl mb-2">Connexion sécurisée…</h1>
                        <p className="text-foreground/65 text-sm">
                            Vérification de votre identité Google et activation de votre session African Valley.
                        </p>
                    </>
                ) : (
                    <>
                        <div className="h-16 w-16 rounded-full bg-amber-50 text-amber-600 mx-auto flex items-center justify-center mb-5">
                            <AlertCircle size={32} />
                        </div>
                        <h1 className="font-heading text-2xl mb-3">Authentification interrompue</h1>
                        <p className="text-foreground/70 text-sm mb-6">{error}</p>
                        <button onClick={() => navigate('/auth/signin')}
                            className="text-sm text-primary hover:underline"
                            data-testid="auth-callback-retry">
                            ← Retour à la connexion
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
